'use client';

import { Button } from '@ui';
import Dropdown from '@ui/Dropdown';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CellEditor from './CellEditor';
import { ImportField, ImportValuesPatch, patchFor } from './types';

interface Props {
    fields: ImportField[];
    selectedCount: number;
    totalCount: number;
    onApply: (patch: ImportValuesPatch, onlySelected: boolean) => void;
}

/** Picks one form field and a value, then writes it into the selected rows or all of them. */
const BulkSetBar = ({ fields, selectedCount, totalCount, onApply }: Props) => {
    const { t } = useTranslation('bookings');
    const [fieldName, setFieldName] = useState<string | null>(null);
    const [value, setValue] = useState<any>(null);

    const field = fields.find((candidate) => candidate.name === fieldName) ?? null;

    if (fields.length === 0) return null;

    const apply = (onlySelected: boolean) => {
        if (!field) return;
        onApply(patchFor(`form_data.${field.name}`, value), onlySelected);
    };

    return (
        <div className="tw-flex tw-flex-wrap tw-items-start tw-gap-2 tw-rounded-xl tw-bg-accents-1 tw-p-3">
            <span className="tw-self-center tw-text-sm tw-text-accents-7">
                {t('booking.documentImport.bulk.label')}
            </span>
            <div className="tw-w-56">
                <Dropdown
                    label={field ? field.label || field.name : t('booking.documentImport.bulk.chooseField')}
                    flat
                    color="light"
                    block
                    size="sm"
                >
                    {fields.map((candidate) => (
                        <Dropdown.Item
                            key={candidate.name}
                            label={candidate.label || candidate.name}
                            onClick={() => {
                                setFieldName(candidate.name);
                                setValue(null);
                            }}
                        />
                    ))}
                </Dropdown>
            </div>
            {field && (
                <div className="tw-min-w-[200px] tw-flex-1">
                    {/* keyed, so switching the field starts with an empty editor */}
                    <CellEditor
                        key={field.name}
                        field={field}
                        columnKey={`form_data.${field.name}`}
                        value={value}
                        onChange={setValue}
                    />
                </div>
            )}
            <Button size="sm" flat isDisabled={!field || selectedCount === 0} onPress={() => apply(true)}>
                {t('booking.documentImport.bulk.applySelected', { count: selectedCount })}
            </Button>
            <Button size="sm" isDisabled={!field || totalCount === 0} onPress={() => apply(false)}>
                {t('booking.documentImport.bulk.applyAll', { count: totalCount })}
            </Button>
        </div>
    );
};

export default BulkSetBar;
